import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO_URL = "/trenta-logo.png";
const PEPDIRECT_URL = "https://pepdirect.co";

const EXCLUSIVITY_LINE =
  "PepDirect is the sole authorized distributor of Trenta Medical compounds. We do not sell direct. All retail and volume orders are fulfilled exclusively through PepDirect.";

const anchorLinks = [
  { hash: "#what-we-do", label: "What We Do" },
  { hash: "#quality", label: "Quality & Testing" },
  { hash: "#authenticity", label: "Authenticity" },
];

export default function Layout({ children }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToAnchor = (e, hash) => {
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate(`/${hash}`);
    } else {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#1A2B5E" }}>
      {/* HEADER */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? "shadow-[0_2px_24px_rgba(0,0,0,0.3)]" : ""
        }`}
        style={{
          background: "#1A2B5E",
          borderBottom: scrolled
            ? "1px solid rgba(255,255,255,0.12)"
            : "1px solid transparent",
        }}
        data-testid="site-header"
      >
        <div className="container mx-auto px-5 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Link to="/" data-testid="logo-link" className="flex-shrink-0 block">
              <img
                src={LOGO_URL}
                alt="Trenta Medical"
                style={{
                  width: 130,
                  height: "auto",
                  display: "block",
                  filter: "brightness(0) invert(1)",
                }}
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
              {anchorLinks.map(({ hash, label }) => (
                <a
                  key={hash}
                  href={`/${hash}`}
                  onClick={(e) => goToAnchor(e, hash)}
                  className="text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-[#8FA8E8]"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                  data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {label}
                </a>
              ))}
              <Link
                to="/contact"
                className="text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-[#8FA8E8]"
                style={{
                  color:
                    location.pathname === "/contact"
                      ? "#8FA8E8"
                      : "rgba(255,255,255,0.85)",
                }}
                data-testid="nav-contact"
              >
                Contact
              </Link>
              <a
                href={PEPDIRECT_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-white ml-2 px-6 py-2.5 text-sm font-semibold tracking-wide inline-flex items-center gap-1.5"
                data-testid="nav-pepdirect-cta"
              >
                Order via PepDirect
                <ArrowUpRight size={14} />
              </a>
            </nav>

            {/* Mobile Hamburger */}
            <button
              className="lg:hidden p-2 transition-colors"
              style={{ color: "#FFFFFF" }}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="lg:hidden overflow-hidden"
              style={{
                background: "#1A2B5E",
                borderTop: "1px solid rgba(255,255,255,0.12)",
              }}
              data-testid="mobile-menu"
            >
              <div className="container mx-auto px-5 py-4 flex flex-col">
                {anchorLinks.map(({ hash, label }) => (
                  <a
                    key={hash}
                    href={`/${hash}`}
                    onClick={(e) => goToAnchor(e, hash)}
                    className="text-base font-semibold py-3 border-b transition-colors duration-200 hover:text-[#8FA8E8]"
                    style={{
                      color: "rgba(255,255,255,0.85)",
                      borderColor: "rgba(255,255,255,0.12)",
                    }}
                    data-testid={`mobile-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {label}
                  </a>
                ))}
                <Link
                  to="/contact"
                  onClick={() => setMobileOpen(false)}
                  className="text-base font-semibold py-3 border-b transition-colors duration-200 hover:text-[#8FA8E8]"
                  style={{
                    color: "rgba(255,255,255,0.85)",
                    borderColor: "rgba(255,255,255,0.12)",
                  }}
                  data-testid="mobile-nav-contact"
                >
                  Contact
                </Link>
                <a
                  href={PEPDIRECT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-white mt-4 px-5 py-3 text-sm font-semibold tracking-wide inline-flex items-center justify-center gap-1.5"
                  data-testid="mobile-nav-pepdirect-cta"
                >
                  Order via PepDirect
                  <ArrowUpRight size={14} />
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* MAIN */}
      <main className="flex-1" data-testid="main-content">
        {children}
      </main>

      {/* FOOTER */}
      <footer style={{ background: "#1A2B5E" }} data-testid="site-footer">
        {/* Condensed exclusivity band */}
        <div
          className="py-5"
          style={{ background: "#12204A", borderBottom: "1px solid rgba(255,255,255,0.12)" }}
          data-testid="footer-exclusivity-band"
        >
          <div className="container mx-auto px-5 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="label-mono mb-1.5" style={{ color: "#8FA8E8" }}>
                § Exclusive Distribution
              </p>
              <p className="text-sm text-white leading-relaxed max-w-3xl">
                {EXCLUSIVITY_LINE}
              </p>
            </div>
            <a
              href={PEPDIRECT_URL}
              target="_blank"
              rel="noreferrer"
              className="btn-white flex-shrink-0 inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold"
              data-testid="footer-pepdirect-link"
            >
              Visit pepdirect.co
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>

        <div className="container mx-auto px-5 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
            <div>
              <img
                src={LOGO_URL}
                alt="Trenta Medical"
                style={{
                  width: 130,
                  height: "auto",
                  display: "block",
                  marginBottom: 16,
                  filter: "brightness(0) invert(1)",
                }}
              />
              <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.6)" }}>
                Netherlands-based manufacturer of research-grade peptides and
                compounds, supplying the UK and EU. Synthesis, nitrogen-sealed
                fill-finish, and independent quality control.
              </p>
            </div>
            <div>
              <p className="label-mono mb-5" style={{ color: "#8FA8E8" }}>
                § Contact
              </p>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <a
                    href="mailto:volume@trentamedical.com"
                    className="transition-colors duration-200 hover:text-white"
                    style={{ color: "rgba(255,255,255,0.6)" }}
                    data-testid="footer-email-link"
                  >
                    volume@trentamedical.com
                  </a>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="transition-colors duration-200 hover:text-white"
                    style={{ color: "rgba(255,255,255,0.6)" }}
                    data-testid="footer-contact-link"
                  >
                    General &amp; press enquiries
                  </Link>
                </li>
                <li>
                  <a
                    href={PEPDIRECT_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="transition-colors duration-200 hover:text-white"
                    style={{ color: "rgba(255,255,255,0.6)" }}
                    data-testid="footer-pepdirect-nav-link"
                  >
                    Purchasing · pepdirect.co ↗
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <p className="label-mono mb-5" style={{ color: "#8FA8E8" }}>
                § Disclaimer
              </p>
              <p
                className="text-sm leading-relaxed"
                style={{ color: "rgba(255,255,255,0.6)" }}
                data-testid="footer-disclaimer"
              >
                All compounds are intended exclusively for laboratory research
                use. Not for human or veterinary consumption.
              </p>
            </div>
          </div>
          <div
            className="pt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2"
            style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}
          >
            <p className="text-xs" style={{ color: "rgba(255,255,255,0.35)" }}>
              &copy; {new Date().getFullYear()} Trenta Medical. All rights
              reserved. Netherlands.
            </p>
            <p className="text-xs" style={{ color: "rgba(255,255,255,0.35)" }}>
              Research use only. Not for human or veterinary consumption.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
